// @flow
import { forwardRef } from "react";
import styled from "styled-components";
import { ErrorMessage } from "@nested/brand";

const RadioCircle = styled.span`
  background-color: white;
  border-radius: 15px;
  border: 1px solid ${({ theme }) => theme.palette.hague40};
  display: inline-block;
  height: 15px;
  margin-right: 15px;
  position: relative;
  top: 2px;
  visibility: visible;
  width: 15px;

  input:checked + & {
    background-color: white;
    border: 1px solid ${({ theme }) => theme.palette.hague80};
  }

  input:focus + &,
  input:active + & {
    background-color: white;
    border: 1px solid ${({ theme }) => theme.palette.terracotta100};
  }

  input + &::after {
    content: "";
    height: 11px;
    width: 11px;
    border-radius: 11px;
    position: absolute;
    top: 2px;
    right: 2px;
    background-color: transparent;
  }

  input:checked:not(:focus) + &::after {
    background-color: ${({ theme }) => theme.palette.hague80};
  }

  input:focus:checked + &::after,
  input:checked:active + &::after {
    background-color: ${({ theme }) => theme.palette.terracotta100};
  }
`;

const LabelText = styled.span`
  display: inline-block;
  width: calc(100% - 36px);
  vertical-align: text-top;
`;

const RadioGroup = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
`;

const RadioLabel = styled.label`
  padding: 15px;
  cursor: pointer;
  user-select: none;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.palette.terracotta60 : theme.palette.hague40};
  transition: border 200ms ease;

  &:first-child {
    border-top-right-radius: 6px;
    border-top-left-radius: 6px;
  }

  &:last-child {
    border-bottom-right-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  &:not(:first-child) {
    border-top: 0;
  }
`;

const RadioInput = styled.input`
  /* 
  Styles to visually hide whilst not impairing use by screenreaders 
  https://a11yproject.com/posts/how-to-hide-content/
  */
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
`;

export type RadioButtonsOptions<T> = Array<{|
  label: string,
  value: T,
|}>;

type Props = {
  "data-test"?: string,
  hasErrors?: boolean,
  errorMessage?: string,
  name: string,
  options: RadioButtonsOptions<any>,
  value: ?any,
  onChange: (value: any) => void,
};

export const RadioButtons = forwardRef<Props, *>(
  (
    {
      "data-test": dataTest,
      errorMessage,
      hasErrors,
      name,
      onChange,
      options,
      value: selected,
    },
    ref,
  ) => {
    return (
      <>
        <RadioGroup data-test={dataTest}>
          {options.map(({ value, label }, index) => {
            const isChecked = selected === value;

            let innerRef = null;

            if (isChecked) {
              innerRef = ref;
            } else if (index === 0) {
              innerRef = ref;
            }

            return (
              <RadioLabel
                hasError={hasErrors}
                key={index}
                data-test={`${name}-label-${String(value)}`}
              >
                <RadioInput
                  type="radio"
                  name={name}
                  value={value}
                  onChange={() => onChange(value)}
                  checked={isChecked}
                  ref={innerRef}
                />
                <RadioCircle />
                <LabelText>{label}</LabelText>
              </RadioLabel>
            );
          })}
        </RadioGroup>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </>
    );
  },
);
